import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
  Svg,
  Path,
  Circle,
} from "@react-pdf/renderer";
import { BRAND, LOGOS } from "../../lib/defaultFlyer";

// Register fonts (served from /public/fonts)
Font.register({
  family: "Chewy",
  src: `${window.location.origin}/fonts/Chewy-Regular.ttf`,
});
Font.register({
  family: "Carlito",
  fonts: [
    { src: `${window.location.origin}/fonts/Carlito-Regular.ttf`, fontWeight: 400 },
    { src: `${window.location.origin}/fonts/Carlito-Bold.ttf`, fontWeight: 700 },
    { src: `${window.location.origin}/fonts/Carlito-Italic.ttf`, fontStyle: "italic" },
  ],
});
Font.registerHyphenationCallback((word) => [word]);

const s = StyleSheet.create({
  page: {
    paddingTop: 28,
    paddingBottom: 24,
    paddingHorizontal: 32,
    fontFamily: "Carlito",
    color: BRAND.ink,
    backgroundColor: BRAND.paper,
    fontSize: 9.5,
    lineHeight: 1.35,
  },

  // ---------------- Header ----------------
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  logoImg: { width: 60, height: 60, objectFit: "contain" },
  headerRight: { alignItems: "flex-end" },
  brandWord: {
    fontFamily: "Chewy",
    fontSize: 20,
    lineHeight: 1.4,
    color: BRAND.forest,
    letterSpacing: 1.2,
    paddingBottom: 2,
  },
  tagline: {
    fontFamily: "Chewy",
    fontSize: 13,
    lineHeight: 1.4,
    color: BRAND.forest,
    letterSpacing: 0.8,
  },

  // ---------------- Hero ----------------
  hero: {
    backgroundColor: BRAND.forest,
    borderRadius: 12,
    padding: 16,
    color: BRAND.cream,
    flexDirection: "row",
    overflow: "hidden",
  },
  heroLeft: { flex: 1, paddingRight: 12 },
  eyebrow: {
    fontSize: 8,
    color: BRAND.sage,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    fontWeight: 700,
    marginBottom: 8,
  },
  headline: {
    fontFamily: "Chewy",
    fontSize: 22,
    lineHeight: 1.2,
    color: BRAND.cream,
    marginBottom: 10,
  },
  subhead: {
    fontSize: 10,
    color: "#D6DED7",
    lineHeight: 1.45,
    maxWidth: 340,
  },
  heroRight: {
    width: 90,
    alignItems: "center",
    justifyContent: "center",
  },

  // ---------------- Benefits ----------------
  benefitsRow: {
    flexDirection: "row",
    marginTop: 10,
    gap: 8,
  },
  benefitCard: {
    flex: 1,
    backgroundColor: BRAND.cream,
    borderRadius: 8,
    padding: 9,
    borderLeft: `3px solid ${BRAND.forest}`,
  },
  benefitTitle: {
    fontFamily: "Chewy",
    fontSize: 11.5,
    lineHeight: 1.25,
    color: BRAND.forest,
    marginBottom: 5,
  },
  benefitBody: { fontSize: 8.5, color: "#3A4F47", lineHeight: 1.4 },

  // ---------------- Stats ----------------
  statsRow: {
    flexDirection: "row",
    marginTop: 10,
    backgroundColor: BRAND.bone,
    borderRadius: 8,
    padding: 9,
  },
  statCell: {
    flex: 1,
    alignItems: "center",
    borderRight: `1px solid ${BRAND.sage}`,
    paddingHorizontal: 4,
  },
  statCellLast: {
    flex: 1,
    alignItems: "center",
    paddingHorizontal: 4,
  },
  statValue: {
    fontFamily: "Chewy",
    fontSize: 18,
    lineHeight: 1.2,
    color: BRAND.forest,
  },
  statLabel: {
    fontSize: 7.5,
    color: "#3A4F47",
    textTransform: "uppercase",
    letterSpacing: 0.6,
    marginTop: 4,
    textAlign: "center",
  },

  // ---------------- Section title ----------------
  sectionTitle: {
    fontFamily: "Chewy",
    fontSize: 13,
    lineHeight: 1.25,
    color: BRAND.forest,
    marginTop: 11,
    marginBottom: 7,
  },

  // ---------------- Process ----------------
  processRow: { flexDirection: "row", gap: 8 },
  processCard: {
    flex: 1,
    padding: 9,
    borderRadius: 8,
    border: `1px solid ${BRAND.sage}`,
    backgroundColor: BRAND.paper,
  },
  processStep: {
    fontFamily: "Chewy",
    color: BRAND.sage,
    fontSize: 15,
    lineHeight: 1.2,
    marginBottom: 4,
  },
  processTitle: {
    fontFamily: "Chewy",
    fontSize: 11,
    lineHeight: 1.25,
    color: BRAND.forest,
    marginBottom: 5,
  },
  processBody: { fontSize: 8.5, color: "#3A4F47", lineHeight: 1.4 },

  // ---------------- Requirements ----------------
  reqRow: { flexDirection: "row", gap: 8 },
  reqCol: {
    flex: 1,
    backgroundColor: BRAND.cream,
    borderRadius: 8,
    padding: 9,
  },
  reqHeading: {
    fontFamily: "Chewy",
    fontSize: 11,
    lineHeight: 1.25,
    color: BRAND.forest,
    marginBottom: 6,
  },
  reqItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 2,
  },
  reqDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: BRAND.forest,
    marginTop: 4,
    marginRight: 5,
  },
  reqText: { fontSize: 9, color: "#3A4F47", flex: 1 },

  // ---------------- CTA / Contact ----------------
  ctaBox: {
    marginTop: 10,
    backgroundColor: BRAND.forest,
    borderRadius: 10,
    padding: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  ctaText: { flex: 1, paddingRight: 12 },
  ctaHeadline: {
    fontFamily: "Chewy",
    fontSize: 14,
    lineHeight: 1.2,
    color: BRAND.cream,
    marginBottom: 5,
  },
  ctaSub: { fontSize: 9, color: "#C7D2CA", lineHeight: 1.4 },
  contactCol: { minWidth: 160 },
  contactLine: { fontSize: 9, color: BRAND.cream, marginBottom: 1.5 },
  contactName: {
    fontFamily: "Chewy",
    fontSize: 11.5,
    lineHeight: 1.2,
    color: BRAND.cream,
    marginBottom: 4,
  },

  // ---------------- Footer ----------------
  footer: {
    marginTop: 8,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: 6,
    borderTop: `1px solid ${BRAND.sage}`,
  },
  footerText: {
    fontSize: 7.5,
    color: BRAND.sage,
    letterSpacing: 1,
    textTransform: "uppercase",
  },
  pawRow: { flexDirection: "row", gap: 4 },
});

const Paw = ({ color = BRAND.sage, size = 10 }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Circle cx="6" cy="8" r="2.2" fill={color} />
    <Circle cx="12" cy="5.5" r="2.2" fill={color} />
    <Circle cx="18" cy="8" r="2.2" fill={color} />
    <Circle cx="8" cy="13.5" r="2.2" fill={color} />
    <Circle cx="16" cy="13.5" r="2.2" fill={color} />
    <Path
      d="M7 19 C7 15.5 9 14 12 14 C15 14 17 15.5 17 19 C17 21 15.5 22 12 22 C8.5 22 7 21 7 19 Z"
      fill={color}
    />
  </Svg>
);

const Bubble = ({ size, x, y, opacity = 0.18 }) => (
  <View
    style={{
      position: "absolute",
      left: x,
      top: y,
      width: size,
      height: size,
      borderRadius: size / 2,
      backgroundColor: BRAND.sage,
      opacity,
    }}
  />
);

const resolveLogo = (flyer) => {
  if (flyer.customLogo) return flyer.customLogo;
  const found = LOGOS.find((l) => l.id === flyer.logoId);
  const path = (found || LOGOS[0]).src;
  return `${window.location.origin}${path}`;
};

const FlyerDocument = ({ flyer }) => {
  const logoSrc = resolveLogo(flyer);

  return (
    <Document title={flyer.name} author="Messy Pups">
      <Page size="A4" style={s.page}>
        {/* HEADER */}
        <View style={s.header}>
          <Image src={logoSrc} style={s.logoImg} />
          <View style={s.headerRight}>
            <Text style={s.tagline}>{flyer.tagline}</Text>
          </View>
        </View>

        {/* HERO */}
        <View style={s.hero}>
          <Bubble size={70} x={-20} y={-20} opacity={0.12} />
          <Bubble size={40} x={310} y={130} opacity={0.16} />
          <Bubble size={26} x={250} y={-10} opacity={0.2} />
          <View style={s.heroLeft}>
            <Text style={s.eyebrow}>{flyer.eyebrow}</Text>
            <Text style={s.headline}>{flyer.headline}</Text>
            <Text style={s.subhead}>{flyer.subheadline}</Text>
          </View>
          <View style={s.heroRight}>
            <Image src={logoSrc} style={{ width: 78, height: 78 }} />
          </View>
        </View>

        {/* BENEFITS */}
        <View style={s.benefitsRow}>
          {flyer.benefits.map((b, i) => (
            <View key={i} style={s.benefitCard}>
              <Text style={s.benefitTitle}>{b.title}</Text>
              <Text style={s.benefitBody}>{b.body}</Text>
            </View>
          ))}
        </View>

        {/* STATS */}
        <View style={s.statsRow}>
          {flyer.stats.map((st, i) => (
            <View
              key={i}
              style={i === flyer.stats.length - 1 ? s.statCellLast : s.statCell}
            >
              <Text style={s.statValue}>{st.value}</Text>
              <Text style={s.statLabel}>{st.label}</Text>
            </View>
          ))}
        </View>

        {/* PROCESS */}
        <Text style={s.sectionTitle}>How it works</Text>
        <View style={s.processRow}>
          {flyer.process.map((p, i) => (
            <View key={i} style={s.processCard}>
              <Text style={s.processStep}>{p.step}</Text>
              <Text style={s.processTitle}>{p.title}</Text>
              <Text style={s.processBody}>{p.body}</Text>
            </View>
          ))}
        </View>

        {/* REQUIREMENTS */}
        <Text style={s.sectionTitle}>The deal</Text>
        <View style={s.reqRow}>
          <View style={s.reqCol}>
            <Text style={s.reqHeading}>You provide</Text>
            {flyer.requirements.youProvide.map((it, i) => (
              <View key={i} style={s.reqItem}>
                <View style={s.reqDot} />
                <Text style={s.reqText}>{it}</Text>
              </View>
            ))}
          </View>
          <View style={s.reqCol}>
            <Text style={s.reqHeading}>We provide</Text>
            {flyer.requirements.weProvide.map((it, i) => (
              <View key={i} style={s.reqItem}>
                <View style={s.reqDot} />
                <Text style={s.reqText}>{it}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* CTA / CONTACT */}
        <View style={s.ctaBox}>
          <View style={s.ctaText}>
            <Text style={s.ctaHeadline}>Let&apos;s talk</Text>
            <Text style={s.ctaSub}>{flyer.cta}</Text>
          </View>
          <View style={s.contactCol}>
            <Text style={s.contactName}>{flyer.contact.name}</Text>
            {flyer.contact.role ? (
              <Text style={s.contactLine}>{flyer.contact.role}</Text>
            ) : null}
            {flyer.contact.email ? (
              <Text style={s.contactLine}>{flyer.contact.email}</Text>
            ) : null}
            {flyer.contact.phone ? (
              <Text style={s.contactLine}>{flyer.contact.phone}</Text>
            ) : null}
            {flyer.contact.website ? (
              <Text style={s.contactLine}>{flyer.contact.website}</Text>
            ) : null}
            {flyer.contact.location ? (
              <Text style={s.contactLine}>{flyer.contact.location}</Text>
            ) : null}
          </View>
        </View>

        {/* FOOTER */}
        <View style={s.footer}>
          <Text style={s.footerText}>{flyer.footerNote}</Text>
          <View style={s.pawRow}>
            <Paw color={BRAND.sage} />
            <Paw color={BRAND.sage} />
            <Paw color={BRAND.sage} />
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default FlyerDocument;
